import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Search, X, Building2, TrendingUp, User, FileText, BriefcaseBusiness, ReceiptText, Workflow, Navigation } from 'lucide-react'
import FocusTrap from 'focus-trap-react'
import { apiClient } from '@/lib/api'
import { Avatar } from '@/components/ui/Avatar'
import { Badge, type BadgeColor } from '@/components/ui/Badge'
import type { ApiContact, ApiCompany, ApiDeal, ApiQuote, ApiJob, ApiInvoice, ApiWorkflow, PaginatedApiResponse } from '@/lib/apiTypes'
import { logger } from '../../lib/logger'
import { SafeAreaTop } from '@/components/ui/SafeAreaTop'

// ─────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean
  onClose: () => void
}

type ResultSection = 'contact' | 'company' | 'deal' | 'quote' | 'job' | 'invoice' | 'workflow' | 'destination'

interface FlatResult {
  id: string
  section: ResultSection
  label: string
  sub?: string
  path?: string
}

interface DestinationResult {
  id: string
  label: string
  sub: string
  path: string
  keywords: string
}

// ─────────────────────────────────────────────────────────
// Deal stage color helper
// ─────────────────────────────────────────────────────────

type DealStage = ApiDeal['stage']

const stageColors: Record<DealStage, BadgeColor> = {
  LEAD:        'slate',
  QUALIFIED:   'slateblue',
  PROPOSAL:    'yellow',
  NEGOTIATION: 'gold',
  WON:         'moss',
  LOST:        'red',
}

const stageLabels: Record<DealStage, string> = {
  LEAD:        'Lead',
  QUALIFIED:   'Qualified',
  PROPOSAL:    'Proposal',
  NEGOTIATION: 'Negotiation',
  WON:         'Won',
  LOST:        'Lost',
}

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000)     return `$${(value / 1_000).toFixed(0)}K`
  return `$${value.toFixed(0)}`
}

export function getGlobalSearchPath(result: Pick<FlatResult, 'id' | 'section' | 'path'>): string {
  if (result.path) return result.path
  if (result.section === 'contact') return `/customers/people/${result.id}`
  if (result.section === 'company') return `/customers/companies/${result.id}`
  if (result.section === 'deal') return `/work/${encodeURIComponent(`deal:${result.id}`)}`
  if (result.section === 'quote') return `/work/${encodeURIComponent(`q:${result.id}`)}`
  if (result.section === 'job') return `/work/${encodeURIComponent(`job:${result.id}`)}`
  if (result.section === 'invoice') return `/work/${encodeURIComponent(`inv:${result.id}`)}`
  if (result.section === 'workflow') return '/workflows'
  return '/'
}

// ─────────────────────────────────────────────────────────
// Cached data store (module-level so it persists across opens)
// ─────────────────────────────────────────────────────────

let cachedContacts: ApiContact[] | null = null
let cachedCompanies: ApiCompany[] | null = null
let cachedDeals: ApiDeal[] | null = null
let cachedQuotes: ApiQuote[] | null = null
let cachedJobs: ApiJob[] | null = null
let cachedInvoices: ApiInvoice[] | null = null
let cachedWorkflows: ApiWorkflow[] | null = null
let fetchPromise: Promise<void> | null = null

const destinations: DestinationResult[] = [
  { id: 'dashboard', label: 'Dashboard', sub: 'Overview, revenue, schedule', path: '/dashboard', keywords: 'dashboard overview home revenue schedule' },
  { id: 'work', label: 'Work', sub: 'Leads, quotes, jobs, invoices', path: '/work', keywords: 'work leads deals quotes jobs invoices funnel pipeline' },
  { id: 'customers', label: 'Customers', sub: 'People and companies', path: '/customers', keywords: 'customers people contacts companies clients' },
  { id: 'calendar', label: 'Calendar', sub: 'Schedule jobs', path: '/calendar', keywords: 'calendar schedule jobs appointments agenda' },
  { id: 'inbox', label: 'Inbox', sub: 'Messages, campaigns, forms', path: '/inbox', keywords: 'inbox messages texts email campaigns forms' },
  { id: 'workflows', label: 'Workflows', sub: 'Automations and history', path: '/workflows', keywords: 'workflows automations automation triggers actions history' },
  { id: 'marketing', label: 'Marketing', sub: 'Reviews and social posts', path: '/marketing', keywords: 'marketing reviews social posts campaigns' },
  { id: 'reports', label: 'Reports', sub: 'Performance and finance', path: '/reports', keywords: 'reports analytics charts revenue invoices jobs' },
  { id: 'settings', label: 'Settings', sub: 'Account and business setup', path: '/settings', keywords: 'settings account setup profile team billing phone twilio a2p brand security notifications' },
  { id: 'phone-settings', label: 'Phone Settings', sub: 'Numbers, routing, AI assistant', path: '/settings/phone', keywords: 'phone twilio numbers routing assistant calls sms' },
  { id: 'brand-settings', label: 'Brand Kit', sub: 'Logo, colors, quote PDF identity', path: '/settings/brand', keywords: 'brand kit logo colors pdf quote invoice' },
  { id: 'team-settings', label: 'Team Settings', sub: 'Users and permissions', path: '/settings/team', keywords: 'team users invite roles permissions' },
]

function readItems<T>(
  result: PromiseSettledResult<{ data: { data: PaginatedApiResponse<T> } }>,
): T[] {
  if (result.status !== 'fulfilled') return []
  return result.value.data.data.items ?? []
}

async function fetchAllData(): Promise<void> {
  if (fetchPromise) return fetchPromise
  fetchPromise = (async () => {
    try {
      const [contactsRes, companiesRes, dealsRes, quotesRes, jobsRes, invoicesRes, workflowsRes] = await Promise.allSettled([
        apiClient.get('/api/contacts', { params: { take: 100 } }),
        apiClient.get('/api/companies', { params: { take: 100 } }),
        apiClient.get('/api/deals', { params: { take: 100 } }),
        apiClient.get('/api/quotes', { params: { take: 100 } }),
        apiClient.get('/api/jobs', { params: { take: 100 } }),
        apiClient.get('/api/invoices', { params: { take: 100 } }),
        apiClient.get('/api/workflows', { params: { take: 100 } }),
      ])
      cachedContacts = readItems<ApiContact>(contactsRes)
      cachedCompanies = readItems<ApiCompany>(companiesRes)
      cachedDeals = readItems<ApiDeal>(dealsRes)
      cachedQuotes = readItems<ApiQuote>(quotesRes)
      cachedJobs = readItems<ApiJob>(jobsRes)
      cachedInvoices = readItems<ApiInvoice>(invoicesRes)
      cachedWorkflows = readItems<ApiWorkflow>(workflowsRes)
    } catch {
      // Fail silently — search just returns empty
      cachedContacts = cachedContacts ?? []
      cachedCompanies = cachedCompanies ?? []
      cachedDeals = cachedDeals ?? []
      cachedQuotes = cachedQuotes ?? []
      cachedJobs = cachedJobs ?? []
      cachedInvoices = cachedInvoices ?? []
      cachedWorkflows = cachedWorkflows ?? []
    }
  })()
  return fetchPromise
}

// ─────────────────────────────────────────────────────────
// Search filter helpers
// ─────────────────────────────────────────────────────────

function matchesQuery(haystack: string, query: string): boolean {
  return haystack.toLowerCase().includes(query.toLowerCase())
}

function contactName(contact?: { firstName: string; lastName: string } | null): string {
  return contact ? `${contact.firstName} ${contact.lastName}`.trim() : ''
}

function filterContacts(contacts: ApiContact[], query: string): ApiContact[] {
  if (!query) return []
  return contacts
    .filter(c =>
      matchesQuery(`${c.firstName} ${c.lastName} ${c.email ?? ''} ${c.phone ?? ''} ${c.company?.name ?? ''}`, query)
    )
    .slice(0, 5)
}

function filterCompanies(companies: ApiCompany[], query: string): ApiCompany[] {
  if (!query) return []
  return companies
    .filter(c => matchesQuery(`${c.name} ${c.industry ?? ''}`, query))
    .slice(0, 5)
}

function filterDeals(deals: ApiDeal[], query: string): ApiDeal[] {
  if (!query) return []
  return deals
    .filter(d => matchesQuery(`${d.name} ${d.company?.name ?? ''} ${contactName(d.contact)} ${d.stage}`, query))
    .slice(0, 5)
}

function filterQuotes(quotes: ApiQuote[], query: string): ApiQuote[] {
  if (!query) return []
  return quotes
    .filter(q => matchesQuery(`${q.quoteNumber ?? ''} ${contactName(q.contact)} ${q.company?.name ?? ''} ${q.serviceAddress ?? ''} ${q.status}`, query))
    .slice(0, 5)
}

function filterJobs(jobs: ApiJob[], query: string): ApiJob[] {
  if (!query) return []
  return jobs
    .filter(j => matchesQuery(`${j.address} ${contactName(j.contact)} ${j.company?.name ?? ''} ${j.status}`, query))
    .slice(0, 5)
}

function filterInvoices(invoices: ApiInvoice[], query: string): ApiInvoice[] {
  if (!query) return []
  return invoices
    .filter(i => matchesQuery(`${i.invoiceNumber} ${contactName(i.contact)} ${i.company?.name ?? ''} ${i.status} ${i.notes ?? ''}`, query))
    .slice(0, 5)
}

function filterWorkflows(workflows: ApiWorkflow[], query: string): ApiWorkflow[] {
  if (!query) return []
  return workflows
    .filter(w => matchesQuery(`${w.name} ${w.description ?? ''} ${w.isActive ? 'active' : 'inactive'}`, query))
    .slice(0, 5)
}

function filterDestinations(query: string): DestinationResult[] {
  if (!query) return []
  return destinations
    .filter(d => matchesQuery(`${d.label} ${d.sub} ${d.keywords}`, query))
    .slice(0, 5)
}

// ─────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────

// CRM resource roots whose invalidation should refresh the global-search cache (FND-027).
const SEARCH_RESOURCE_ROOTS = ['contacts', 'companies', 'deals', 'quotes', 'jobs', 'invoices', 'workflows'] as const

export default function GlobalSearchModal({ isOpen, onClose }: Props) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [dataReady, setDataReady] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  // Filtered results
  const contacts = dataReady ? filterContacts(cachedContacts ?? [], debouncedQuery) : []
  const companies = dataReady ? filterCompanies(cachedCompanies ?? [], debouncedQuery) : []
  const deals = dataReady ? filterDeals(cachedDeals ?? [], debouncedQuery) : []
  const quotes = dataReady ? filterQuotes(cachedQuotes ?? [], debouncedQuery) : []
  const jobs = dataReady ? filterJobs(cachedJobs ?? [], debouncedQuery) : []
  const invoices = dataReady ? filterInvoices(cachedInvoices ?? [], debouncedQuery) : []
  const workflows = dataReady ? filterWorkflows(cachedWorkflows ?? [], debouncedQuery) : []
  const destinationResults = filterDestinations(debouncedQuery)
  const totalResults =
    contacts.length +
    companies.length +
    deals.length +
    quotes.length +
    jobs.length +
    invoices.length +
    workflows.length +
    destinationResults.length

  const contactResults: FlatResult[] = contacts.map(c => ({
      id: c.id,
      section: 'contact' as ResultSection,
      label: `${c.firstName} ${c.lastName}`,
      sub: c.email,
    }))
  const companyResults: FlatResult[] = companies.map(c => ({
      id: c.id,
      section: 'company' as ResultSection,
      label: c.name,
      sub: c.industry,
    }))
  const dealResults: FlatResult[] = deals.map(d => ({
      id: d.id,
      section: 'deal' as ResultSection,
      label: d.name,
      sub: d.stage,
    }))
  const quoteResults: FlatResult[] = quotes.map(q => ({
      id: q.id,
      section: 'quote' as ResultSection,
      label: q.quoteNumber ?? `Quote ${q.id.slice(-6).toUpperCase()}`,
      sub: `${contactName(q.contact) || q.company?.name || 'Quote'} · ${q.status}`,
    }))
  const jobResults: FlatResult[] = jobs.map(j => ({
      id: j.id,
      section: 'job' as ResultSection,
      label: j.address || contactName(j.contact) || 'Job',
      sub: j.status,
    }))
  const invoiceResults: FlatResult[] = invoices.map(i => ({
      id: i.id,
      section: 'invoice' as ResultSection,
      label: i.invoiceNumber,
      sub: `${contactName(i.contact) || i.company?.name || 'Invoice'} · ${i.status}`,
    }))
  const workflowResults: FlatResult[] = workflows.map(w => ({
      id: w.id,
      section: 'workflow' as ResultSection,
      label: w.name,
      sub: w.isActive ? 'Active workflow' : 'Inactive workflow',
    }))
  const navResults: FlatResult[] = destinationResults.map(d => ({
      id: d.id,
      section: 'destination' as ResultSection,
      label: d.label,
      sub: d.sub,
      path: d.path,
    }))

  // Flat list for keyboard navigation
  const flatResults: FlatResult[] = [
    ...contactResults,
    ...companyResults,
    ...dealResults,
    ...quoteResults,
    ...jobResults,
    ...invoiceResults,
    ...workflowResults,
    ...navResults,
  ]

  // Debounce query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(timer)
  }, [query])

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(-1)
  }, [debouncedQuery])

  // Reset + refetch helper — clears module-level cache then re-runs fetchAllData.
  const resetAndFetch = useCallback(() => {
    cachedContacts = null
    cachedCompanies = null
    cachedDeals = null
    cachedQuotes = null
    cachedJobs = null
    cachedInvoices = null
    cachedWorkflows = null
    fetchPromise = null
    setDataReady(false)
    setLoading(true)
    return fetchAllData()
      .then(() => {
        setDataReady(true)
        setLoading(false)
      })
      .catch((err) => {
        logger.error('[GlobalSearchModal] fetchAllData failed:', err)
        setDataReady(true)   // show empty results rather than infinite spinner
        setLoading(false)
      })
  }, [])

  // Fetch on every open
  useEffect(() => {
    if (!isOpen) return
    void resetAndFetch()
  }, [isOpen, resetAndFetch])

  // FND-027: keep the module-level cache in sync with CRM mutations elsewhere
  // in the app. Subscribe to the TanStack QueryCache and refetch when any
  // resource root the modal depends on is invalidated.
  useEffect(() => {
    if (!isOpen) return
    const cache = queryClient.getQueryCache()
    const unsub = cache.subscribe((event) => {
      if (event.type !== 'updated') return
      // Only react to invalidations of the relevant resource roots.
      const action = event.action as { type?: string } | undefined
      if (action?.type !== 'invalidate') return
      const key = event.query.queryKey
      if (!Array.isArray(key) || typeof key[0] !== 'string') return
      if (!SEARCH_RESOURCE_ROOTS.includes(key[0] as typeof SEARCH_RESOURCE_ROOTS[number])) return
      void resetAndFetch()
    })
    return unsub
  }, [isOpen, queryClient, resetAndFetch])

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 10)
      setQuery('')
      setDebouncedQuery('')
      setActiveIndex(-1)
    }
  }, [isOpen])

  // Body scroll lock while open
  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [isOpen])

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex(i => Math.min(i + 1, flatResults.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex(i => Math.max(i - 1, -1))
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault()
        const result = flatResults[activeIndex]
        if (result) navigateToResult(result)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flatResults, activeIndex]
  )

  function navigateToResult(result: FlatResult) {
    onClose()
    navigate(getGlobalSearchPath(result))
  }

  function navigateTo(section: ResultSection, id?: string) {
    navigateToResult({ id: id ?? '', section, label: '' })
  }

  // Compute global index offset per section for keyboard active state
  const contactOffset = 0
  const companyOffset = contacts.length
  const dealOffset = contacts.length + companies.length
  const quoteOffset = dealOffset + deals.length
  const jobOffset = quoteOffset + quotes.length
  const invoiceOffset = jobOffset + jobs.length
  const workflowOffset = invoiceOffset + invoices.length
  const destinationOffset = workflowOffset + workflows.length

  const TITLE_ID = 'global-search-modal-title'

  const renderResultSection = (
    title: string,
    results: FlatResult[],
    offset: number,
    icon: ReactNode,
  ) => {
    if (loading || results.length === 0) return null
    const hasPrevious = offset > 0
    return (
      <section className="py-2">
        {hasPrevious && <div className="hairline-t mx-4 mb-2" />}
        <p className="eyebrow px-5 py-2">{title}</p>
        {results.map((result, idx) => {
          const globalIdx = offset + idx
          const isActive = globalIdx === activeIndex
          return (
            <button
              key={`${result.section}:${result.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 cursor-pointer rounded-lg transition-colors text-left mx-1 ${
                isActive ? 'bg-white/[0.07]' : 'hover:bg-white/[0.04]'
              }`}
              onClick={() => navigateToResult(result)}
              onMouseEnter={() => setActiveIndex(globalIdx)}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.70)' }}>
                {icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink-100 truncate">{result.label}</p>
                {result.sub && <p className="text-xs text-white/40 truncate">{result.sub}</p>}
              </div>
            </button>
          )
        })}
      </section>
    )
  }

  if (!isOpen) return null

  return (
    <FocusTrap active={isOpen} focusTrapOptions={{ returnFocusOnDeactivate: true, allowOutsideClick: true }}>
      {/* Overlay — also serves as the dialog root */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        className="fixed inset-0 bg-obsidian-950/60 backdrop-blur-sm z-[80] flex flex-col md:flex-row md:items-start md:justify-center md:pt-24"
        onClick={onClose}
      >
        {/* Panel — stop click propagation so clicking inside doesn't close.
            SafeAreaTop owns env(safe-area-inset-top). */}
        <SafeAreaTop
          className="glass-popup w-full overflow-hidden md:max-w-2xl md:mx-4 md:rounded-2xl rounded-none"
          onClick={e => e.stopPropagation()}
        >
          {/* Visually hidden title for aria-labelledby */}
          <span id={TITLE_ID} className="sr-only">Global search</span>

          {/* Search input row */}
          <div className="flex items-center gap-3 px-5 py-4">
            <Search className="w-5 h-5 text-ink-400 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search anything..."
              className="flex-1 text-xl bg-transparent text-ink-50 outline-none placeholder:text-white/40"
              autoComplete="off"
              spellCheck={false}
            />
            <div className="flex items-center gap-2 shrink-0">
              {/* X always visible: clears query if non-empty, closes modal if already empty */}
              <button
                onClick={() => {
                  if (query) {
                    setQuery('')
                    inputRef.current?.focus()
                  } else {
                    onClose()
                  }
                }}
                aria-label={query ? 'Clear search' : 'Close search'}
                className="p-1 text-white/40 hover:text-ink-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
              <kbd className="text-xs bg-white/[0.06] px-2 py-0.5 rounded text-white/40 border border-white/[0.08] font-mono hidden sm:inline">
                ESC
              </kbd>
            </div>
          </div>

          {/* Divider */}
          <div className="hairline-t" />

          {/* Results */}
          <div ref={listRef} className="max-h-[480px] overflow-y-auto">
            {/* Loading state */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="w-5 h-5 rounded-full border-2 border-white/20 border-t-white/60 animate-spin" />
              </div>
            )}

            {/* Initial hint */}
            {!loading && !debouncedQuery && (
              <div className="flex flex-col items-center gap-2 py-12 text-center">
                <Search className="w-8 h-8 text-white/30" />
                <p className="text-sm text-white/40">Search contacts, work, invoices, workflows, settings...</p>
                <p className="text-xs text-white/30">Type to get started</p>
              </div>
            )}

            {/* No results */}
            {!loading && debouncedQuery && totalResults === 0 && (
              <div className="flex flex-col items-center gap-2 py-12 text-center">
                <p className="text-sm text-ink-400">
                  No results for{' '}
                  <span className="text-ink-200 font-medium">"{debouncedQuery}"</span>
                </p>
                <p className="text-xs text-white/30">Try a different search term</p>
              </div>
            )}

            {/* Contacts section */}
            {!loading && contacts.length > 0 && (
              <section className="py-2">
                <p className="eyebrow px-5 py-2">Contacts</p>
                {contacts.map((contact, idx) => {
                  const globalIdx = contactOffset + idx
                  const isActive = globalIdx === activeIndex
                  return (
                    <button
                      key={contact.id}
                      className={`w-full flex items-center gap-3 px-4 py-3 cursor-pointer rounded-lg transition-colors text-left mx-1 ${
                        isActive ? 'bg-white/[0.07]' : 'hover:bg-white/[0.04]'
                      }`}
                      onClick={() => navigateTo('contact', contact.id)}
                      onMouseEnter={() => setActiveIndex(globalIdx)}
                    >
                      <Avatar
                        name={`${contact.firstName} ${contact.lastName}`}
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink-100 truncate">
                          {contact.firstName} {contact.lastName}
                        </p>
                        {contact.email && (
                          <p className="text-xs text-white/40 truncate">{contact.email}</p>
                        )}
                      </div>
                      {contact.company && (
                        <span className="text-xs text-white/40 shrink-0 truncate max-w-[120px]">
                          {contact.company.name}
                        </span>
                      )}
                    </button>
                  )
                })}
              </section>
            )}

            {/* Companies section */}
            {!loading && companies.length > 0 && (
              <section className="py-2">
                {contacts.length > 0 && <div className="hairline-t mx-4 mb-2" />}
                <p className="eyebrow px-5 py-2">Companies</p>
                {companies.map((company, idx) => {
                  const globalIdx = companyOffset + idx
                  const isActive = globalIdx === activeIndex
                  return (
                    <button
                      key={company.id}
                      className={`w-full flex items-center gap-3 px-4 py-3 cursor-pointer rounded-lg transition-colors text-left mx-1 ${
                        isActive ? 'bg-white/[0.07]' : 'hover:bg-white/[0.04]'
                      }`}
                      onClick={() => navigateTo('company', company.id)}
                      onMouseEnter={() => setActiveIndex(globalIdx)}
                    >
                      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-slateblue-600/20 to-slateblue-600/10 border border-slateblue-500/20 shrink-0">
                        <Building2 className="w-4 h-4 text-slateblue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink-100 truncate">{company.name}</p>
                        {company.industry && (
                          <p className="text-xs text-white/40 truncate">{company.industry}</p>
                        )}
                      </div>
                      {company._count?.contacts !== undefined && (
                        <span className="text-xs text-white/40 shrink-0">
                          {company._count.contacts} contact{company._count.contacts !== 1 ? 's' : ''}
                        </span>
                      )}
                    </button>
                  )
                })}
              </section>
            )}

            {/* Deals section */}
            {!loading && deals.length > 0 && (
              <section className="py-2">
                {(contacts.length > 0 || companies.length > 0) && (
                  <div className="hairline-t mx-4 mb-2" />
                )}
                <p className="eyebrow px-5 py-2">Deals</p>
                {deals.map((deal, idx) => {
                  const globalIdx = dealOffset + idx
                  const isActive = globalIdx === activeIndex
                  return (
                    <button
                      key={deal.id}
                      className={`w-full flex items-center gap-3 px-4 py-3 cursor-pointer rounded-lg transition-colors text-left mx-1 ${
                        isActive ? 'bg-white/[0.07]' : 'hover:bg-white/[0.04]'
                      }`}
                      onClick={() => navigateTo('deal', deal.id)}
                      onMouseEnter={() => setActiveIndex(globalIdx)}
                    >
                      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(181,211,240,0.12)', border: '0.5px solid rgba(181,211,240,0.20)' }}>
                        <TrendingUp className="w-4 h-4" style={{ color: '#B5D3F0' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink-100 truncate">{deal.name}</p>
                        {deal.company && (
                          <p className="text-xs text-white/40 truncate">{deal.company.name}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge color={stageColors[deal.stage]}>
                          {stageLabels[deal.stage]}
                        </Badge>
                        <span className="text-sm font-medium text-ink-200">
                          {formatCurrency(deal.value)}
                        </span>
                      </div>
                    </button>
                  )
                })}
              </section>
            )}

            {renderResultSection('Quotes', quoteResults, quoteOffset, <FileText className="w-4 h-4" />)}
            {renderResultSection('Jobs', jobResults, jobOffset, <BriefcaseBusiness className="w-4 h-4" />)}
            {renderResultSection('Invoices', invoiceResults, invoiceOffset, <ReceiptText className="w-4 h-4" />)}
            {renderResultSection('Workflows', workflowResults, workflowOffset, <Workflow className="w-4 h-4" />)}
            {renderResultSection('Go To', navResults, destinationOffset, <Navigation className="w-4 h-4" />)}

            {/* Bottom padding */}
            {!loading && totalResults > 0 && <div className="h-2" />}
          </div>

          {/* Footer hint */}
          {!loading && totalResults > 0 && (
            <>
              <div className="hairline-t" />
              <div className="flex items-center gap-4 px-5 py-2.5">
                <div className="flex items-center gap-1.5">
                  <User className="w-3 h-3 text-white/30" />
                  <span className="text-xs text-white/30">{totalResults} result{totalResults !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-1 ml-auto">
                  <kbd className="text-xs bg-white/[0.06] px-1.5 py-0.5 rounded text-white/40 border border-white/[0.08] font-mono">↑↓</kbd>
                  <span className="text-xs text-white/30">navigate</span>
                  <kbd className="text-xs bg-white/[0.06] px-1.5 py-0.5 rounded text-white/40 border border-white/[0.08] font-mono ml-2">↵</kbd>
                  <span className="text-xs text-white/30">open</span>
                </div>
              </div>
            </>
          )}
        </SafeAreaTop>
      </div>
    </FocusTrap>
  )
}
